import { Link } from 'react-router-dom';


function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 id={id} className="text-lg font-bold text-gray-900 mb-3 scroll-mt-24">{title}</h2>
      <div className="space-y-3 text-sm text-gray-600 leading-relaxed">{children}</div>
    </section>
  );
}

export function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="mx-auto max-w-6xl flex items-center justify-between px-6 py-3">
          <Link to="/">
            <img src="/assets/logo/logo-full-dark-text.png" alt="WallaWhats" className="h-9" />
          </Link>
          <Link to="/signup" className="rounded-full bg-primary-600 px-5 py-2 text-xs font-semibold text-white hover:bg-primary-700">
            Sign up free
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-6xl px-6 py-12 sm:py-16">
        <article>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">Privacy Policy</h1>
          <p className="mt-3 text-sm text-gray-500">Last updated: March 21, 2026</p>
          <p className="mt-6 text-sm text-gray-600 leading-relaxed">
            This Privacy Policy explains how <strong>Blockchain Web Services Inc.</strong> ("BWS", "we", "us", or "our"), operating the WallaWhats platform, collects, uses, and protects your personal information when you use our X/Twitter monitoring and alert service.
          </p>

          <Section id="information-we-collect" title="1. Information We Collect">
            <p><strong>Account Information.</strong> When you create an account we collect your email address, WhatsApp phone number (or other alert destinations you register), and authentication credentials. If you sign in via Google or GitHub, we receive your profile name and email from the identity provider — we do not receive or store your social account passwords.</p>
            <p><strong>Usage Data.</strong> We automatically collect information about how you interact with the service, including: X (Twitter) accounts you subscribe to, alert delivery events (timestamp, status, channel), API key usage, and access timestamps.</p>
            <p><strong>Third-Party Platform Data.</strong> To deliver alerts we fetch publicly-posted content from the X API for the accounts you explicitly subscribe to. We do not collect private messages, followers, or any non-public data from X.</p>
            <p><strong>Payment Information.</strong> If you subscribe to a paid plan, payment processing is handled entirely by Stripe. We receive your subscription status and plan type but do not store credit card numbers or banking details.</p>
            <p><strong>Technical Data.</strong> We collect standard web server logs including IP addresses, browser type, and referring pages. AWS CloudFront and API Gateway generate these logs for operational purposes.</p>
          </Section>

          <Section id="how-we-use" title="2. How We Use Your Information">
            <p>We use your information to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Provide, maintain, and improve the WallaWhats service</li>
              <li>Authenticate your identity and manage your account</li>
              <li>Deliver real-time alerts to your registered WhatsApp, SMS, or email destinations</li>
              <li>Process subscriptions and billing via Stripe</li>
              <li>Enforce plan limits (subscription count, monthly alert quota, velocity caps)</li>
              <li>Send transactional messages (verification codes, plan changes)</li>
              <li>Monitor service health and prevent abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
            <p>We do not sell, rent, or share your personal information with third parties for marketing purposes.</p>
          </Section>

          <Section id="third-party-providers" title="3. Third-Party Messaging Providers">
            <p><strong>WhatsApp (Meta).</strong> Alerts delivered via WhatsApp pass through Meta's WhatsApp Business Platform. Your phone number and the alert content are shared with Meta solely for message delivery, subject to Meta's privacy policy.</p>
            <p><strong>Email (Amazon SES) and SMS (Amazon Pinpoint / Twilio).</strong> Alert content and your destination address are shared with the respective provider for delivery only.</p>
            <p><strong>X (Twitter) API.</strong> We query X's public API using our platform credentials to monitor the accounts you subscribe to. Your identity is not shared with X in this flow.</p>
          </Section>

          <Section id="sms" title="4. SMS Channel">
            <p><strong>What we collect.</strong> If you add an SMS channel, we collect the phone number you provide and use it solely to send you (a) one-time verification codes when you add or re-verify the channel, and (b) tweet alerts for the X accounts you subscribe to.</p>
            <p><strong>Consent.</strong> SMS messages are sent only after you explicitly opt in by adding the number on your account at <a href="https://wallawhats.com/channels" className="text-primary-600 hover:underline">wallawhats.com/channels</a>. We do not share your phone number with affiliates or marketing partners.</p>
            <p><strong>Frequency.</strong> Message frequency is driven by your own subscription list — you receive an alert for each new tweet from accounts you've chosen to monitor, plus a one-time verification code when you add a channel. Your monthly plan caps the maximum number of alerts you can receive.</p>
            <p><strong>Opt out.</strong> Reply <strong>STOP</strong> to any WallaWhats SMS to unsubscribe; the carrier will confirm and we will stop sending to that number. Reply <strong>HELP</strong> for assistance. You can also delete the SMS channel at any time from your account dashboard. Standard message and data rates from your carrier may apply.</p>
            <p><strong>Retention.</strong> Phone numbers are retained while the SMS channel is active on your account; deleting the channel removes the number from our records within 30 days, except where retention is required by law (e.g. delivery records for abuse-prevention).</p>
          </Section>

          <Section id="data-storage" title="5. Data Storage and Security">
            <p>Your account data, subscription list, and notification history are stored in AWS DynamoDB in the US East (N. Virginia) region.</p>
            <p>We implement industry-standard security measures including:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Encryption in transit (TLS/HTTPS for all connections)</li>
              <li>Authentication via AWS Cognito with JWT tokens</li>
              <li>API key authentication with hashed storage</li>
              <li>AWS IAM role-based access controls</li>
              <li>Secrets Manager for credential storage</li>
            </ul>
          </Section>

          <Section id="third-party" title="6. Third-Party Services">
            <p>We use the following third-party services that may process your data:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Amazon Web Services (AWS)</strong> — infrastructure hosting, authentication (Cognito), data storage (DynamoDB), content delivery (CloudFront)</li>
              <li><strong>Stripe</strong> — payment processing for paid subscriptions</li>
              <li><strong>Google</strong> — OAuth authentication (if you choose to sign in with Google)</li>
              <li><strong>GitHub</strong> — OAuth authentication (if you choose to sign in with GitHub)</li>
              <li><strong>Solution Network</strong> — decentralized file storage and distribution</li>
            </ul>
            <p>Each third-party service is governed by its own privacy policy.</p>
          </Section>

          <Section id="cookies" title="7. Cookies and Local Storage">
            <p>We use essential cookies and browser local storage for:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Authentication session tokens (AWS Cognito)</li>
              <li>CSRF protection during OAuth flows</li>
            </ul>
            <p>We do not use advertising or tracking cookies. We do not use any third-party analytics services.</p>
          </Section>

          <Section id="your-rights" title="8. Your Rights">
            <p>You have the right to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Access</strong> your personal data stored in your account</li>
              <li><strong>Correct</strong> inaccurate information via your profile settings</li>
              <li><strong>Delete</strong> your account and associated data by contacting us</li>
              <li><strong>Export</strong> your subscriptions and notification history via the API</li>
              <li><strong>Withdraw consent</strong> for optional data processing at any time</li>
            </ul>
            <p>Note that deleting your account removes your data from our systems but does not retroactively recall alerts already delivered to your registered destinations or to third-party messaging providers (Meta / WhatsApp, email, SMS).</p>
          </Section>

          <Section id="data-retention" title="9. Data Retention">
            <p>We retain your account data for as long as your account is active. If you delete your account, we remove your personal data within 30 days, except where retention is required by law. Notification history and alert-delivery metadata may be retained for operational records and abuse-prevention purposes. Message contents delivered by third-party providers (Meta / WhatsApp, email, SMS) are governed by those providers' retention policies.</p>
          </Section>

          <Section id="children" title="10. Children's Privacy">
            <p>WallaWhats is not directed at children under the age of 16. We do not knowingly collect personal information from children. If you believe a child has provided us with personal data, please contact us and we will delete it.</p>
          </Section>

          <Section id="changes" title="11. Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on this page with a revised "Last updated" date. Your continued use of the service after changes constitutes acceptance of the updated policy.</p>
          </Section>

          <Section id="contact" title="12. Contact">
            <p>For questions about this Privacy Policy or to exercise your rights, contact us at:</p>
            <p className="mt-2">
              <strong>Blockchain Web Services Inc.</strong><br />
              Support email: <a href="mailto:support@wallawhats.com" className="text-primary-600 hover:text-primary-500">support@wallawhats.com</a><br />
              Website: <a href="https://wallawhats.com" className="text-primary-600 hover:text-primary-500">wallawhats.com</a> · <a href="https://www.bws.ninja" className="text-primary-600 hover:text-primary-500" target="_blank" rel="noopener noreferrer">www.bws.ninja</a>
            </p>
          </Section>
        </article>

        {/* Footer nav */}
        <div className="mt-16 pt-8 border-t border-gray-200 flex items-center justify-between text-sm text-gray-500">
          <Link to="/" className="hover:text-gray-900">Back to WallaWhats</Link>
          <Link to="/terms-of-service" className="hover:text-gray-900">Terms of Service</Link>
        </div>
      </main>
    </div>
  );
}
